import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Cpu, HardDrive, Wifi, Zap, ChevronDown, ChevronUp } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import Reveal from "@/components/marketing/Reveal";
import api from "@/lib/api";
import { BRAND } from "@/branding.config";

const CATEGORIES = [
  { key: "forex",      label: "Forex VPS",        sub: "Latency-optimized for MT4/MT5 & cTrader"    },
  { key: "windows",    label: "Windows VPS",      sub: "Licensed Windows Server for any workload"   },
  { key: "dedicated",  label: "Dedicated servers",sub: "Bare-metal for regulated workloads"         },
  { key: "enterprise", label: "Enterprise",       sub: "Custom SLA, MSA, dedicated hypervisors"     },
];

const CYCLES = [
  { key: "monthly",   label: "Monthly",   div: 1,  suffix: "/mo", discount: 0 },
  { key: "quarterly", label: "Quarterly", div: 3,  suffix: "/mo", discount: 8 },
  { key: "yearly",    label: "Yearly",    div: 12, suffix: "/mo", discount: 17 },
];

const CURRENCIES = { USD: { symbol: "$", rate: 1 }, EUR: { symbol: "€", rate: 0.92 }, GBP: { symbol: "£", rate: 0.78 } };

const DEDICATED = [
  { slug: "bare-metal-64",  name: "Bare Metal 64",  cpu: 64,  ram_gb: 512,  storage_gb: 4096, bandwidth_tb: 50, price_monthly: 890,  features: ["EPYC 9004 series", "1× 25GbE uplink", "IPMI included"] },
  { slug: "bare-metal-128", name: "Bare Metal 128", cpu: 128, ram_gb: 1024, storage_gb: 8192, bandwidth_tb: 100,price_monthly: 1690, features: ["EPYC 9004 series", "2× 25GbE uplink", "Redundant PSU"], popular: true },
  { slug: "bare-metal-256", name: "Bare Metal 256", cpu: 256, ram_gb: 2048, storage_gb: 16384,bandwidth_tb: 200,price_monthly: 2890, features: ["Dual EPYC", "2× 100GbE uplink", "Private cross-connect"] },
];

const FEATURE_MATRIX = [
  { label: "Sub-1ms broker link",     values: ["Add-on",     "✓",         "✓",         "✓"] },
  { label: "NVMe storage",            values: ["✓",          "✓",         "✓",         "✓"] },
  { label: "DDoS protection",         values: ["Standard",   "Standard",  "Premium",   "Custom"] },
  { label: "Dedicated cores",         values: ["Shared",     "Shared",    "✓",         "✓"] },
  { label: "SLA",                     values: ["99.9%",      "99.99%",    "99.99%",    "99.999%"] },
  { label: "Support",                 values: ["24/7 ticket","24/7 chat", "24/7 phone","Named CSM"] },
  { label: "Snapshots",               values: ["Weekly",     "Daily",     "Hourly",    "Continuous"] },
  { label: "MSA / DPA on request",    values: ["—",          "—",         "—",         "✓"] },
];

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [active, setActive] = useState("forex");
  const [currency, setCurrency] = useState("USD");
  const [showMatrix, setShowMatrix] = useState(false);

  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data)); }, []);

  const cyc = CYCLES.find((c) => c.key === cycle);
  const cur = CURRENCIES[currency];
  const fmt = (usd) => `${cur.symbol}${(usd * cur.rate).toFixed(2)}`;

  const items = active === "dedicated"
    ? DEDICATED
    : plans.filter((p) => p.category === active);
  const isEnterprise = active === "enterprise";

  return (
    <>
      <HeadMeta title="Pricing" description={`Transparent pricing for ${BRAND.app_name} VPS, Windows Server, dedicated hardware and enterprise deployments.`} />
      <div className="relative">
        <GradientBg variant="soft" />

        <section className="relative mx-auto max-w-7xl px-6 pt-24 pb-6 text-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Pricing</div>
            <h1 className="mt-3 text-5xl md:text-7xl font-black tracking-tight">Simple. Powerful. Fair.</h1>
            <p className="mt-4 max-w-xl mx-auto text-muted-foreground">Choose a workload, pick a cycle, pay only for what you deploy.</p>
          </Reveal>

          <div className="mt-10 flex flex-col items-center gap-4">
            <div className="glass rounded-full p-1 inline-flex flex-wrap justify-center gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`pricing-tab-${c.key}`}
                  onClick={() => setActive(c.key)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                    active === c.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >{c.label}</button>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="inline-flex rounded-full border border-white/[0.08] p-1">
                {CYCLES.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCycle(c.key)}
                    data-testid={`pricing-cycle-${c.key}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      cycle === c.key ? "bg-white/[0.06] text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >{c.label}{c.discount ? <span className="ml-1 text-emerald-500">−{c.discount}%</span> : ""}</button>
                ))}
              </div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="pricing-currency"
                className="rounded-full bg-transparent border border-white/[0.08] px-3 py-1.5 text-xs font-mono focus:border-primary outline-none"
              >
                {Object.keys(CURRENCIES).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-16">
          {isEnterprise ? (
            <Reveal>
              <div className="glass-strong p-10 md:p-16 text-center relative overflow-hidden">
                <div className="orb bg-violet w-[420px] h-[420px] -left-32 -top-32" />
                <div className="orb bg-pink w-[320px] h-[320px] -right-20 -bottom-20" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Enterprise</div>
                  <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">Priced against your architecture, not a menu.</h2>
                  <p className="mt-4 max-w-2xl mx-auto text-muted-foreground">
                    Dedicated hardware, custom SLA, MSA + DPA, procurement-friendly paperwork.
                    Volume discounts start at $10k / month committed spend.
                  </p>
                  <div className="mt-8">
                    <Link to="/contact" className="btn-primary px-6 py-3">Talk to solutions</Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {items.map((p, i) => {
                const priceUsd = active === "dedicated" ? p.price_monthly : (p.price_monthly / (cyc.key === "yearly" ? 1 : 1));
                // Approximate cycle price: monthly base × 12 × (1 - discount%) / cycle months
                const monthly = active === "dedicated" ? p.price_monthly : p.price_monthly;
                const perMonth = monthly * (1 - cyc.discount / 100);
                return (
                  <Reveal key={p.slug} delay={i * 0.1}>
                    <div className={`glass p-8 h-full relative overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${p.popular ? "border-primary/40 glow-violet" : ""}`}>
                      {p.popular && (
                        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/25">
                          Most popular
                        </span>
                      )}
                      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">
                        {active} {p.locations ? `· ${p.locations.length} regions` : ""}
                      </div>
                      <h3 className="mt-1.5 text-3xl font-black tracking-tight">{p.name}</h3>
                      <div className="mt-5 flex items-baseline gap-1">
                        <span className="font-display text-5xl font-black">{fmt(perMonth)}</span>
                        <span className="text-sm text-muted-foreground">{cyc.suffix}</span>
                      </div>
                      {cyc.discount > 0 && <div className="text-xs text-muted-foreground">Save {cyc.discount}% billed {cyc.label.toLowerCase()}</div>}
                      <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                        <Spec icon={Cpu}       label={`${p.cpu} ${active === "dedicated" ? "cores" : "vCPU"}`} />
                        <Spec icon={HardDrive} label={`${p.ram_gb} GB RAM`} />
                        <Spec icon={HardDrive} label={`${p.storage_gb} GB NVMe`} />
                        <Spec icon={Wifi}      label={`${p.bandwidth_tb} TB traffic`} />
                      </div>
                      <ul className="mt-6 space-y-2 text-sm">
                        {(p.features || []).map((f) => (
                          <li key={f} className="flex items-center gap-2 text-muted-foreground">
                            <Check size={14} className="text-emerald-500" /> {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/register"
                        data-testid={`plan-${p.slug}-cta`}
                        className={`mt-8 w-full ${p.popular ? "btn-primary" : "btn-outline"} py-3`}
                      >
                        <Zap size={14} /> {active === "dedicated" ? "Reserve hardware" : `Deploy ${p.name}`}
                      </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-24">
          <button
            onClick={() => setShowMatrix(!showMatrix)}
            data-testid="pricing-matrix-toggle"
            className="btn-outline"
          >
            {showMatrix ? <ChevronUp size={14}/> : <ChevronDown size={14}/>} Feature comparison table
          </button>
          {showMatrix && (
            <Reveal>
              <div className="mt-6 surface overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[11px] uppercase tracking-widest text-muted-foreground bg-white/[0.03]">
                    <tr>
                      <th className="text-left px-6 py-4">Feature</th>
                      {CATEGORIES.map((c) => (
                        <th key={c.key} className="text-left px-6 py-4">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {FEATURE_MATRIX.map((row) => (
                      <tr key={row.label} className="border-t border-white/[0.06]">
                        <td className="px-6 py-3 font-semibold">{row.label}</td>
                        {row.values.map((v, i) => (
                          <td key={i} className="px-6 py-3 text-muted-foreground">{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          )}
        </section>
      </div>
    </>
  );
}

function Spec({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon size={14} className="text-primary" /> {label}
    </div>
  );
}
